import { Injectable, inject } from '@angular/core';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Logger } from '@df/core/service/logger';
import { Url } from '@df/core/service/url';
import { Basket } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { Prefs } from '@df/session/prefs';
import { PriceService, TaxArea, TaxMode } from '@df/ui/price/price.service';
import { Nexus } from '@tomandco/nexus-api-types';
import { NModuleGtm } from '../df-module-gtm.interfaces';
import { Gtm } from './../event/gtm.event';

interface IExtras {
    quote_id?: number;
    coupon?: string;
    shipping_tier?: string;
    payment_type?: string;
}

@Injectable({
    providedIn: 'root'
})
export class GtmDataService {
    /**
     * Customer basket
     */
    private basket = inject(Basket);

    /**
     * User preferences
     */
    private prefsService = inject(Prefs);

    /**
     * Product resource
     */
    private productResource = inject(ProductResource);

    /**
     * Logger
     */
    private logger = inject(Logger);

    /**
     * Attribute resource
     */
    private attributeResource = inject(AttributeResource);

    /**
     * Price service
     */
    private priceService = inject(PriceService);

    /**
     * GTM module config
     */
    private moduleConfig = inject(NModuleGtm.OPTIONS);

    /**
     * App config service
     */
    private dfApiConfigService = inject(DfApiConfigService);

    private url = inject(Url);

    /**
     * Tax mode for prices
     */
    protected taxMode = this.moduleConfig.includeTax ? TaxMode.including : TaxMode.excluding;

    /**
     * Retrives ecommerce state
     */
    async getEventData(entryItems: (Gtm.BasketItem | Gtm.ListItem | Gtm.Item)[], extras?: IExtras): Promise<Gtm.Ecommerce.Content> {
        if (!this.dfApiConfigService.apiConfig) {
            await this.dfApiConfigService.loadApiConfig();
        }
        const items = (
            await Promise.all(
                entryItems.map(async (item, index) => {
                    const data = await this.getProductDataFromItem(item);
                    if (!data) {
                        return;
                    }
                    if ('price' in item) {
                        Object.assign(data, this.getProductBasketContext(item));
                    }
                    if ('position' in item) {
                        Object.assign(data, this.getProductListContext(item));
                    }
                    return data;
                })
            )
        ).filter(i => !!i) as Gtm.Ecommerce.Product[];

        const ecommerce = this.wrapItems(items);
        if (extras) {
            Object.assign(ecommerce, extras);
        }

        return ecommerce;
    }

    /**
     * Retrives promotion ecommerce state
     */
    getPromotionEventData(promo: Gtm.Promotion): Gtm.Ecommerce.PromotionContent {
        return {
            creative_name: promo.creativeName,
            creative_slot: promo.creativeSlot,
            promotion_id: promo.promotionId,
            promotion_name: promo.promotionName
        };
    }

    /**
     * Wrap items into ecommerce content
     */
    wrapItems(items: Gtm.Ecommerce.Content['items']): Gtm.Ecommerce.Content {
        return {
            currency: this.prefsService.getCurrencyCode(),
            value: Math.round(items.reduce((acc, item) => acc + item.price * (item.quantity || 1), 0) * 100) / 100,
            items
        };
    }

    /**
     * Converts basket item to gtm item change data
     */
    basketItemToGtmItem(item: BasketItem): Gtm.BasketItem {
        const taxPercent = item.data.tax || 0;
        const taxAmount = item.data['tax_amount'] || 0;
        return {
            product: item.product!,
            source: item,
            discount: Math.round(100 * item.getDiscount(this.taxMode)) / 100,
            price: item.price,
            taxPercent,
            taxAmount,
            qty: item.qty
        };
    }

    /**
     * Gets basket items converted to gtm items
     */
    getBasketItems() {
        return this.basket.items.map(i => this.basketItemToGtmItem(i));
    }

    /**
     * Gets basket items converted to gtm items
     */
    getBasketExtras(checkoutData?: Nexus.Api.Checkout.Load.Response['checkout']): IExtras {
        const result: IExtras = {};
        const couponData = this.basket.data.coupon;
        const coupon = Array.isArray(couponData) ? couponData.join() : couponData;
        result.quote_id = this.basket.data.quote_id;
        if (coupon) {
            result.coupon = coupon;
        }
        const shippingMethod = checkoutData?.quote.shipping.method;
        if (shippingMethod) {
            result.shipping_tier = shippingMethod;
        }

        const paymentMethod = checkoutData?.quote.payment.method as string | undefined; // TODO handle number variant properly

        if (paymentMethod) {
            result.payment_type = paymentMethod;
        }

        return result;
    }

    /**
     * Converts basket item to gtm item change data
     */
    orderItemToGtmItem(item: Nexus.Lib.Customer.Order.HistoryOrderItem): Gtm.BasketItem {
        const taxAmount = item.tax_amount || 0;
        const taxPercent = item.tax_percent || 0;
        return {
            product: this.productResource.get(item.product_id)! as Product<IProductDataV3>,
            source: item,
            discount: this.priceService.applyTax(
                item.discount_amount,
                (taxAmount * item.discount_amount) / item.price,
                this.taxMode,
                TaxArea.basket
            ),
            taxAmount,
            taxPercent,
            price: item.price,
            qty: item.qty_ordered
        };
    }

    async getProductDataFromItem(item: Gtm.BasketItem | Gtm.ListItem | Gtm.Item): Promise<Gtm.Ecommerce.Product | undefined> {
        return this.getProductData(item.product as Product<IProductDataV2>);
    }

    /**
     * Converts product to gtm product data
     */
    async getProductData(product: Product<IProductDataV2>): Promise<Gtm.Ecommerce.Product | undefined> {
        if (!product) {
            return undefined;
        }
        await product.load(product.verbosity);
        if (!product.data) {
            return undefined;
        }

        const price = product.getPrice(undefined, this.taxMode);
        const orgPrice = product.getOrgPrice(undefined, this.taxMode);

        const parentProduct = product.directParent;

        // parent needs to be loaded for child tracking
        if (parentProduct) {
            await parentProduct.load(2);
        }

        const topProduct = parentProduct || product;
        const data = topProduct.data!;

        if (!data.sku) {
            this.logger.error('Missing product sku in GTM event', product);
        }

        if (!topProduct.name) {
            this.logger.error('Missing product name in GTM event', product);
        }

        const item_variant = await this.getProductVariant(product);

        const item: Gtm.Ecommerce.Product = {
            // sku
            item_id: data.sku!,
            // product name
            item_name: topProduct.name,
            // this can be used as a dedicated “main” category of a product or can be used in a hierarchy which means this would be the first category
            // item_category?: string,
            // //  this can be used as a second category, second taxonomy or can be used in a hierarchy as second level
            // item_category2?: string,
            // // this can be used as a third category, additional taxonomy or can be used in a hierarchy as third level
            // item_category3?: string,
            // // this can be used as a fourth category, additional taxonomy or can be used in a hierarchy as fourth level
            // item_category4?: string,
            // //  this can be used as a fifth category, additional  taxonomy or can be used in a hierarchy as fifth level
            // item_category5?: string,
            // //  the item variant, or unique code / description of the additional items
            item_variant,

            // simple product sku
            item_variant_id: product.data?.sku,

            // the unit price of the item in the same currency as the event,
            // If the item has a discount applied, this should be the price AFTER discount and add the discount amount in the discount parameter
            price,

            // differnce of price vs org price (if any)
            discount: orgPrice && orgPrice > price ? orgPrice - price : 0,

            // qty field
            quantity: 1
            // --- PROMOTION PRODUCTS ONLY

            // // name of the element - usually image/video name or Alt name
            // creative_name?: string,
            // //  name of the slot associated with the creative , ex: plp banner insert
            // creative_slot?: string,
            // // Id of the prom notion ( campaign) associated with the item
            // promotion_id?: string,
            // // name of the promotion( campaign_ associated with the item
            // promotion_name?: string,
        };

        const url = product.data?.url;
        if (url) {
            item.url = this.url.toUrl(url);
        }
        const imageUrl = product.data?.image;
        if (imageUrl) {
            item.image_url = this.url.toMediaUrl(imageUrl);
        }

        const brand = topProduct.brand;
        if (brand) {
            // product brand usually collected from a brand attribute at product level or hardcoded if business only sells one brand
            item.item_brand = brand;
        }
        return item;
    }

    /**
     * Gets simple product variant
     */
    private async getProductVariant(product: Product<IProductDataV2>): Promise<string | undefined> {
        const parent = product.directParent as Product<IProductDataV2>;

        // only if product is simple with parent
        if (!parent) {
            return undefined;
        }

        // make sure parent is loaded
        await parent.load(2);

        // take configurable options (ordered)
        const attrCodes = product.configurableAttributeCodes;

        // if parent has no configurable attributes lets pass child sku as a variant
        if (!attrCodes) {
            return product.data!.sku;
        }

        return attrCodes
            .map(code => this.attributeResource.getExisting(code)!.getOptionLabel(product.data![code]!))
            .filter(i => !!i)
            .join(', ');
    }

    /**
     * Gets basket item context for product
     */
    getProductBasketContext(item: Gtm.BasketItem): Gtm.Ecommerce.ProductBasketContext {
        if (typeof item.qty !== 'number') {
            this.logger.error('Missing valid qty GTM event', item);
        }

        if (typeof item.price !== 'number') {
            this.logger.error('Missing valid price in GTM event', item);
        }

        const taxAmount = item.taxAmount;
        const taxPercent = item.taxPercent || 0;

        return {
            // amount of discount associated with the item
            discount: Math.abs(item.discount || 0),
            // the unit price of the item in the same currency as the event,
            // If the item has a discount applied, this should be the price AFTER discount and add the discount amount in the discount parameter
            price: this.priceService.applyTax(
                item.price,
                taxAmount ? (taxAmount > 0 ? taxAmount * -1 : taxAmount) : taxPercent,
                this.taxMode,
                TaxArea.basket
            ),
            // item quantity - this is not required however if not set it will default to 1
            quantity: item.qty
        };
    }

    /**
     * Gets basket item context for product
     */
    getProductListContext(item: Gtm.ListItem): Gtm.Ecommerce.ProductListContext {
        const result: Gtm.Ecommerce.ProductListContext = {
            index: item.position
        };

        if (typeof item.listId === 'string' && item.listId) {
            result.item_list_id = item.listId;
        }

        if (typeof item.listName === 'string' && item.listName) {
            result.item_list_name = item.listName;
        }

        return result;
    }
}
